import { useEffect, useRef, useState } from 'react';

import type { SkillInvocationDetail } from '../lib/api-client';
import { useApiClient } from '../providers/auth-provider';

type Status = 'idle' | 'loading' | 'success' | 'error';

/**
 * ViewModel for the full single-invocation view (including trace). Fetches
 * `GET /invocations/{id}` whenever `invocationId` changes. Returns null
 * detail when no selection is set.
 */
export function useSkillInvocationDetail(invocationId: string | null) {
  const apiClient = useApiClient();
  const [detail, setDetail] = useState<SkillInvocationDetail | null>(null);
  const [status, setStatus] = useState<Status>('idle');
  const [error, setError] = useState<string | undefined>();

  const generationRef = useRef(0);

  useEffect(() => {
    if (!invocationId) {
      setDetail(null);
      setStatus('idle');
      setError(undefined);
      return;
    }
    const gen = ++generationRef.current;
    setStatus('loading');
    setError(undefined);
    apiClient
      .getSkillInvocation(invocationId)
      .then((row) => {
        if (gen !== generationRef.current) return;
        setDetail(row);
        setStatus('success');
      })
      .catch((err: unknown) => {
        if (gen !== generationRef.current) return;
        setError(err instanceof Error ? err.message : 'Failed to load invocation');
        setStatus('error');
      });
  }, [apiClient, invocationId]);

  return { detail, status, error };
}
